import { Injectable } from '@angular/core';
import * as Rx from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BackendFetchErrorTypes } from '../backend.model';
import { Collections } from '../collections.model';
import { RequestDataPayload } from './actions';

export interface RequestStatusWrapper {
  readonly request: RequestDataPayload<any>;
  readonly statusObject: RequestStatus<any>;
}

export interface RequestError<K extends keyof Collections> {
  readonly errorCode: BackendFetchErrorTypes[K];
  readonly requestId?: string;
}

export interface RequestStatus<K extends keyof Collections> {
  readonly ready: boolean;
  readonly error?: RequestError<K>;
}

@Injectable()
export class RequestStatusHandler {
  private _statusStreamSubject$: Rx.Subject<RequestStatusWrapper>;

  constructor() {
    this._statusStreamSubject$ = new Rx.Subject();
  }

  get statusStream$() {
    return this._statusStreamSubject$.asObservable();
  }

  get$<K extends keyof Collections>(
    request: RequestDataPayload<K>,
  ): Rx.Observable<RequestStatus<K>> {
    return this._statusStreamSubject$.pipe(
      filter(e =>
        e.request.clientRequestIds.some(id =>
          request.clientRequestIds.includes(id),
        ),
      ),
      map(e => e.statusObject),
    );
  }

  update<K extends keyof Collections>(
    request: RequestDataPayload<K>,
    status: RequestStatus<K>,
  ) {
    this._statusStreamSubject$.next({
      request,
      statusObject: status,
    });
  }
}

export function requestStatusesEqual<K extends keyof Collections>(
  a: RequestStatus<K>,
  b: RequestStatus<K>,
) {
  return (
    // Plain ready flag, no error
    (a.ready === b.ready && !a.error && !b.error) ||
    // Not ready, error codes equal
    (!a.ready &&
      !b.ready &&
      !!a.error &&
      !!b.error &&
      a.error.errorCode === b.error.errorCode)
  );
}
